import styled from "styled-components";
import React, {useEffect, useState} from "react";
import {ModalBody, ModalFooter, ModalHeader} from "../modal/Modal";
import {useAtom} from "jotai";
import {modalController} from "../../store";

const STATUS_ACCENT = {
  CONFIRMING: 'var(--color-primary-strong)',
  APPROVED: '#178653',
  REJECTED: '#b91c1c',
};

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9"/>
    <path d="m8.5 12.5 2.5 2.5 5-5"/>
  </svg>
);

const WarnIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3 2 21h20L12 3Z"/>
    <path d="M12 9v5M12 17h.01"/>
  </svg>
);

function SelectBox(props){
  const {value, options, onSelect} = props
  const [select, setSelect] = useState()
  const [, setModal] = useAtom(modalController)
  const handleChange = (confirm, targetValue) => {
    if(confirm){
      onSelect(targetValue);
    }else{
      setSelect(value)
    }
    setModal({isShow:false});
  }

  useEffect(() => {
    setSelect(value);

    const escKeyModalClose = (e) => {
      if (e.key === 'Escape') {
        setSelect(value);
        setModal({isShow: false});
      }
    };

    window.addEventListener('keydown', escKeyModalClose);

    return () => {
      window.removeEventListener('keydown', escKeyModalClose);
      setModal({isShow: false});
    };
  }, [value, setModal]);

  const showModal = (e) => {
    const targetValue = e.target.value;
    setSelect(targetValue)
    setModal({
      isShow: true,
      width: 400,
      modalComponent: () => {
        const isReject = targetValue !== 'APPROVED';
        const label = options.find(item => item.value === targetValue)?.label || targetValue;
        const accent = STATUS_ACCENT[targetValue] || 'var(--color-primary-strong)';
        return (
            <div>
              <ModalHeader title={'지면 심사 상태 변경'} closeBtn={false}/>
              <ModalBody>
                <ConfirmIcon style={{color: accent, borderColor: accent + '33', background: accent + '14'}}>
                  {isReject ? <WarnIcon/> : <CheckIcon/>}
                </ConfirmIcon>
                <ConfirmTitle>
                  심사 상태를 <strong style={{color: accent}}>{label}</strong>(으)로<br/>변경할까요?
                </ConfirmTitle>
                <ConfirmDesc $reject={isReject}>
                  {isReject ? '심사반려 시 수정이 불가합니다.' : '심사승인 시 광고가 정상 송출됩니다.'}
                </ConfirmDesc>
              </ModalBody>
              <ModalFooter style={{gap: 8, padding: '14px 20px'}}>
                <DialogCancelButton type="button" onClick={()=>handleChange(false, targetValue)}>취소</DialogCancelButton>
                <DialogConfirmButton type="button" style={{background: accent, borderColor: accent}} onClick={()=>handleChange(true, targetValue)}>확인</DialogConfirmButton>
              </ModalFooter>
            </div>
        )
      }
    })
  }

  const list = options.map((item, key) => {
    return(
      <option key={key} value={item.value}>{item.label}</option>
    )
  })
  return(
    <SelectContainer value={select} multiple={false} onChange={showModal} disabled={select !== 'CONFIRMING'}>{list}</SelectContainer>
  )
}

export default SelectBox

const SelectContainer = styled('select')`
  min-width: 96px;
  height: 30px;
  padding: 0 10px;
  border: 1px solid #dfe3e7;
  border-radius: 7px;
  outline: none;
  background: var(--color-surface);
  color: #45515d;
  font-size: var(--font-size-13);
  font-weight: 650;
  cursor: pointer;
  transition: border-color .15s ease, box-shadow .15s ease;

  &:hover:not(:disabled) {
    border-color: var(--color-primary-focus);
  }

  &:focus {
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, .12);
  }

  &:disabled {
    border-color: #e9edf1;
    background: #f5f6f8;
    color: #9aa3ad;
    cursor: not-allowed;
  }
`

const ConfirmIcon = styled.div`
  display: flex;
  width: 52px;
  height: 52px;
  align-items: center;
  justify-content: center;
  margin: 4px auto 16px;
  border: 1px solid;
  border-radius: 50%;

  svg {
    width: 26px;
    height: 26px;
  }
`

const ConfirmTitle = styled.p`
  margin: 0;
  color: #202a35;
  font-size: var(--font-size-16);
  font-weight: 700;
  line-height: 1.5;
  text-align: center;
`

const ConfirmDesc = styled.p`
  margin: 10px 0 0;
  padding: 10px 14px;
  border-radius: 8px;
  background: ${({$reject}) => ($reject ? '#fef2f2' : '#f0fbf5')};
  color: ${({$reject}) => ($reject ? '#b91c1c' : '#178653')};
  font-size: var(--font-size-13-5);
  line-height: 1.6;
  text-align: center;
`

const DialogCancelButton = styled.button`
  flex: 1;
  height: 42px;
  border: 1px solid #dfe3e7;
  border-radius: 8px;
  background: var(--color-surface);
  color: #56616d;
  font-size: var(--font-size-14-5);
  font-weight: 700;
  cursor: pointer;

  &:hover {
    border-color: #c7ccd2;
    background: #f5f6f8;
  }
`

const DialogConfirmButton = styled.button`
  flex: 1;
  height: 42px;
  border: 1px solid transparent;
  border-radius: 8px;
  color: var(--color-surface);
  font-size: var(--font-size-14-5);
  font-weight: 700;
  cursor: pointer;
  opacity: 1;
  transition: opacity .15s ease;

  &:hover {
    opacity: .88;
  }
`
